import { Grid, Link, SvgIcon, Tooltip } from '@mui/material'
import AgentService from 'api/services/Agent'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { useParams, Link as RouterLink } from 'react-router-dom'
import KeycloakService from 'services/KeycloakService'
import { AgentStatusEnum, PropertyAdStatusEnum } from 'types/enums'
import AgentInfoBox from './AgentInfoBox'
import { ReactComponent as PhoneSvg } from 'assets/svg/new-icons/phone.svg'
import { ReactComponent as MailSvg } from 'assets/svg/new-icons/mail.svg'
import { ReactComponent as SpourgitiSvg } from 'assets/svg/icons/lists/g-spourgiti.svg'
import { ReactComponent as CircledDotsSvg } from 'assets/svg/new-icons/circled-dots.svg'
import { useSnackbar } from 'notistack'
import { CustomTooltip } from 'components/shared/CustomTooltip'

export interface Totals {
  ACTIVE: number
  INACTIVE: number
  INVALID: number
  ALL: number
}

export default function AgentInfos() {
  const { t } = useTranslation()
  const { agentId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const estateAgent = AgentService.hooks.useAgentBasicInfo(agentId, {
    enabled: KeycloakService.isAdmin(),
  })

  function calculate() {
    const totals = { ACTIVE: 0, INACTIVE: 0, INVALID: 0, ALL: 0 }
    if (estateAgent.data?.propertyAdsTotal) {
      estateAgent.data.propertyAdsTotal.forEach((pat) => {
        if (pat.propertyAdStatus === PropertyAdStatusEnum.Active) {
          totals.ACTIVE = pat.total
        } else if (pat.propertyAdStatus === PropertyAdStatusEnum.Inactive) {
          totals.INACTIVE = pat.total
        } else if (pat.propertyAdStatus === PropertyAdStatusEnum.Invalid) {
          totals.INVALID = pat.total
        }
        totals.ALL += pat.total
      })
    }
    return totals
  }

  const constructText = (totals) => {
    return `${t('tables.agents.status.active')}: ${totals.ACTIVE.toLocaleString(
      'el-GR'
    )} \n
    ${t('tables.agents.status.inactive')}: ${totals.INACTIVE.toLocaleString(
      'el-GR'
    )} \n
    ${t('tables.agents.status.invalid')}: ${totals.INVALID.toLocaleString(
      'el-GR'
    )} \n`
  }

  const handleClickOnCopy = (type: string, textToCopy: string) => {
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy)
      enqueueSnackbar(
        type === 'mobile'
          ? t('tables.agents.copy.phoneSuccess')
          : t('tables.agents.copy.mailSuccess'),
        {
          variant: 'success',
        }
      )
    }
  }

  return (
    <Grid
      container
      columnSpacing={'20px'}
      rowSpacing={'5px'}
      sx={{ mt: '40px' }}
      justifyContent={'space-between'}
    >
      {estateAgent.isSuccess ? (
        <>
          <Grid item>
            <AgentInfoBox
              value={
                estateAgent.data?.companyTitle
                  ? estateAgent.data?.companyTitle.toString()
                  : ''
              }
              label={t('tables.agents.columns.name')}
              tooltip=""
            />
          </Grid>
          <Grid item>
            {' '}
            <AgentInfoBox
              value={estateAgent.data?.source ? estateAgent.data?.source : ''}
              label={t('tables.agents.columns.source')}
              tooltip=""
            />
          </Grid>
          <Grid item>
            <AgentInfoBox
              value={
                estateAgent.data?.externalId ? estateAgent.data?.externalId : ''
              }
              label={t('tables.agents.columns.externalId')}
              tooltip=""
            />
          </Grid>
          <Grid item>
            <AgentInfoBox
              value={
                estateAgent.data?.propertyAdsTotal
                  ? `${calculate().ACTIVE.toLocaleString()}/${calculate().ALL.toLocaleString()}`
                  : '0/0'
              }
              label={t('tables.agents.columns.propertyAds')}
              tooltip={constructText(calculate())}
            />
          </Grid>
          <Grid item>
            <AgentInfoBox
              value={
                estateAgent.data?.lastUpdateDate
                  ? DateTime.fromISO(
                      estateAgent.data?.lastUpdateDate
                    ).toLocaleString()
                  : '-'
              }
              label={t('tables.agents.columns.lastUpdate')}
              tooltip=""
            />
          </Grid>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <CustomTooltip
              title={
                estateAgent.data?.mobileNumber
                  ? estateAgent.data?.mobileNumber
                  : ('-' as string)
              }
            >
              <SvgIcon
                onClick={() => {
                  handleClickOnCopy('mobile', estateAgent.data?.mobileNumber)
                }}
                component={PhoneSvg}
                inheritViewBox
                sx={{
                  color: 'black',
                  width: '22px',
                  height: '22px',
                  cursor: 'pointer',
                }}
              />
            </CustomTooltip>
            <CustomTooltip
              title={
                estateAgent.data?.email
                  ? estateAgent.data?.email
                  : ('-' as string)
              }
            >
              <SvgIcon
                component={MailSvg}
                inheritViewBox
                sx={{
                  color: '#ffffff',
                  width: '30px',
                  height: '22px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  handleClickOnCopy('email', estateAgent.data?.email)
                }}
              />
            </CustomTooltip>
            {estateAgent.data.status === AgentStatusEnum.Active ? (
              <Tooltip
                title={
                  t(
                    'tables.agents.columns.actions.external'
                  ) as unknown as string
                }
              >
                <Link
                  href={`${process.env.REACT_APP_REDIRECT_SEARCH_APP_URL}estate-agents/el:${estateAgent.data?.alternativeId}`}
                  underline="none"
                  target="_blank"
                >
                  <SvgIcon
                    component={SpourgitiSvg}
                    inheritViewBox
                    sx={{
                      color: 'black',
                      width: '22px',
                      height: '30px',
                      cursor: 'pointer',
                    }}
                  />
                </Link>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  t(
                    'tables.agents.columns.actions.inactiveAgent'
                  ) as unknown as string
                }
              >
                <SvgIcon
                  component={SpourgitiSvg}
                  inheritViewBox
                  sx={{
                    color: '#D2D2D2',
                    width: '22px',
                    height: '30px',
                    cursor: 'not-allowed',
                  }}
                />
              </Tooltip>
            )}
            <Tooltip
              title={
                t('tables.agents.columns.actions.edit') as unknown as string
              }
            >
              <Link
                component={RouterLink}
                to={'/agents/' + estateAgent.data.id + ''}
                underline="none"
              >
                <SvgIcon
                  component={CircledDotsSvg}
                  inheritViewBox
                  sx={{
                    color: '#ffffff',
                    width: '25px',
                    height: '25px',
                    cursor: 'pointer',
                    mt: '5px',
                  }}
                />
              </Link>
            </Tooltip>
          </Grid>
        </>
      ) : null}
    </Grid>
  )
}
